export interface TokenBootstrapRequest {
  readonly roomName: string;
  readonly participantId: string;
  readonly role?: "follower";
  readonly extraHeaders?: Readonly<Record<string, string>>;
}

export interface TokenBootstrapResponse {
  readonly token: string;
  readonly expiresAt: string;
  readonly roomName?: string;
  readonly livekitUrl?: string;
  readonly role?: string;
  readonly participantId?: string;
}

export interface TokenServiceOptions {
  readonly apiBaseUrl: string;
  readonly mintPath?: string;
  readonly authToken?: string | undefined;
  readonly getAuthToken?: (() => Promise<string | undefined>) | undefined;
  readonly onUnauthorized?: (() => Promise<string | undefined>) | undefined;
  readonly fetchImpl?: typeof fetch;
}

const DEFAULT_MINT_PATH = "/v2/band-sync/livekit/mint-token";

export class TokenService {
  private readonly apiBaseUrl: string;
  private readonly mintPath: string;
  private readonly authToken: string | undefined;
  private readonly getAuthToken: (() => Promise<string | undefined>) | undefined;
  private readonly onUnauthorized: (() => Promise<string | undefined>) | undefined;
  private readonly fetchImpl: typeof fetch;

  constructor(options: TokenServiceOptions) {
    this.apiBaseUrl = options.apiBaseUrl;
    this.mintPath = options.mintPath ?? DEFAULT_MINT_PATH;
    this.authToken = options.authToken;
    this.getAuthToken = options.getAuthToken;
    this.onUnauthorized = options.onUnauthorized;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  private buildEndpoint(): string {
    const trimmedBase = this.apiBaseUrl.trim();
    if (trimmedBase.startsWith("http://") || trimmedBase.startsWith("https://")) {
      const normalizedBase = trimmedBase.endsWith("/") ? trimmedBase.slice(0, -1) : trimmedBase;
      const normalizedPath = this.mintPath.replace(/^\/+/, "");
      return `${normalizedBase}/${normalizedPath}`;
    }

    const normalizedBase = trimmedBase.endsWith("/") ? trimmedBase.slice(0, -1) : trimmedBase;
    return `${normalizedBase}${this.mintPath}`;
  }

  private async resolveAuthToken(): Promise<string | undefined> {
    if (this.getAuthToken) {
      return this.getAuthToken();
    }
    return this.authToken;
  }

  private async postMint(
    endpoint: string,
    request: TokenBootstrapRequest,
    authToken: string | undefined,
  ): Promise<Response> {
    const { extraHeaders, ...requestBody } = request;
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(extraHeaders ?? {}),
    };
    if (authToken) {
      headers.authorization = `Bearer ${authToken}`;
    }
    try {
      return await this.fetchImpl(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`token mint request failed at ${endpoint}: ${message}`);
    }
  }

  async mintToken(request: TokenBootstrapRequest): Promise<TokenBootstrapResponse> {
    const endpoint = this.buildEndpoint();
    const initialToken = await this.resolveAuthToken();
    let response = await this.postMint(endpoint, request, initialToken);
    if (response.status === 401 && this.onUnauthorized) {
      const refreshedToken = await this.onUnauthorized();
      if (refreshedToken) {
        response = await this.postMint(endpoint, request, refreshedToken);
      }
    }
    if (!response.ok) {
      let errorBody = "";
      try {
        errorBody = await response.text();
      } catch {
        errorBody = "";
      }
      const suffix = errorBody ? ` body=${errorBody}` : "";
      throw new Error(`token mint failed with status ${response.status} at ${endpoint}${suffix}`);
    }

    return (await response.json()) as TokenBootstrapResponse;
  }
}
